import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'

import PixelPortrait from '@/components/generals/pixel-portrait'
import PixelCloud from '@/components/pixel/pixel-cloud'

import { TEAM } from '@/constants/content'

const STAT_SEGMENTS = 10

const TeamSection: FC = (): ReactNode => {
    return (
        <section id='team' aria-labelledby='team-title' className='relative overflow-x-clip pt-36 pb-24 md:pt-44 md:pb-36'>
            <PixelCloud className='pointer-events-none absolute top-28 -left-10 w-40 animate-drift [--drift:48px] md:left-[5%] md:w-56' />
            <PixelCloud shape='small' className='pointer-events-none absolute top-40 right-[6%] hidden w-32 animate-drift [--drift:-36px] [--drift-duration:19s] sm:block' />

            <div className='relative mx-auto max-w-7xl px-5 md:px-8'>
                <div className='text-center'>
                    <p className='font-heading text-lg text-bubblegum-deep md:text-xl'>
                        choose your player
                    </p>

                    <h2 id='team-title' className='mt-1 font-heading text-[clamp(3rem,9vw,7.5rem)] leading-[0.85]'>
                        team
                    </h2>

                    <p className='mx-auto mt-6 max-w-lg text-base leading-relaxed font-semibold md:text-lg'>
                        A small crew with a big range. Every player levels up your brand in a different way.
                    </p>
                </div>

                <ul className='mt-16 grid gap-8 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4'>
                    {TEAM.map((member, index) => (
                        <Reveal as='li' key={member.name} delay={index * 120} className='h-full'>
                            <article className='group flex h-full flex-col border-2 border-ink bg-white shadow-pixel transition-[translate,box-shadow] duration-200 ease-[steps(3,end)] hover:-translate-y-1.5 hover:shadow-pixel-lg'>
                                <div className='flex items-center justify-between border-b-2 border-ink bg-ink px-3 py-1.5 font-heading text-sm text-paper'>
                                    <span>player {index + 1}</span>
                                    <span className='text-bubblegum'>ready</span>
                                </div>

                                <PixelPortrait
                                    src={member.image}
                                    alt={`Portrait of ${member.name}, ${member.role}`}
                                    sizes='(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw'
                                    delay={index * 160}
                                    className='aspect-[4/5] border-b-2 border-ink'
                                />

                                <div className='flex flex-1 flex-col p-5'>
                                    <h3 className='font-heading text-2xl leading-none md:text-[1.7rem]'>
                                        {member.name}
                                    </h3>

                                    <p className='mt-1.5 text-sm font-semibold text-muted-foreground'>
                                        {member.role}
                                    </p>

                                    <dl className='mt-4 mb-4 space-y-2'>
                                        {member.stats.map(stat => (
                                            <div key={stat.label} className='grid grid-cols-[5.25rem_1fr] items-center gap-2'>
                                                <dt className='text-[11px] font-bold tracking-wide uppercase'>
                                                    {stat.label}
                                                </dt>

                                                <dd className='flex gap-0.5'>
                                                    <span className='sr-only'>{stat.value} out of {STAT_SEGMENTS}</span>

                                                    {Array.from({ length: STAT_SEGMENTS }, (_, segment) => (
                                                        <span
                                                            key={segment}
                                                            aria-hidden='true'
                                                            className={cn(
                                                                'h-3 flex-1 border border-ink',
                                                                segment < stat.value ? (index % 2 === 0 ? 'bg-bubblegum' : 'bg-fern') : 'bg-white'
                                                            )}
                                                        />
                                                    ))}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>

                                    <p className='mt-auto border-t-2 border-dashed border-ink/25 pt-3 text-sm leading-snug font-medium'>
                                        <span className='font-heading text-bubblegum-deep'>special move:</span>
                                        {' '}
                                        {member.move}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default TeamSection
